import { ProxySession } from "@looker/sdk-rtl";
import axios from "axios";
export class MadLookerSession extends ProxySession {
    /**
     * @param settings: Looker API settings.
     * @param transport: the actual transport layer.
     */
    constructor(sdk, settings, transport) {
        const proxyUrl = sdk.urls.burnsBaseUrl + "/looker/api/token";
        super(settings, proxyUrl, transport);
        this.settings = settings;
        this.transport = transport;
    }
    /**
     * Route token acquisition through an external provider (e.g. a cross-tab
     * postMessage handshake) instead of the burns proxy. Used for external users
     * who have no Smithers session. Clears any cached burns token so the next
     * getToken() call re-authenticates via the provider.
     */
    setExternalAuth(provider) {
        this.externalTokenProvider = provider;
        this.clearCachedToken();
    }
    /** Return to the default burns-backed token source. */
    clearExternalAuth() {
        this.externalTokenProvider = undefined;
        this.clearCachedToken();
    }
    /**
     * Checks if the token needs to be refreshed.
     * Returns true if:
     * - No token exists
     * - No expiration time is tracked
     * - Current time is past the expiration time (with buffer)
     */
    shouldRefreshToken() {
        // No token exists
        if (!this.activeToken) {
            return true;
        }
        // No expiration time tracked (shouldn't happen, but refresh to be safe)
        if (!this.tokenExpiresAt) {
            return true;
        }
        // Token has expired or is about to expire
        if (Date.now() >= this.tokenExpiresAt) {
            return true;
        }
        return false;
    }
    /**
     * Fetches a new token from the API and updates expiration tracking.
     */
    async fetchNewToken() {
        // The only internal/external difference: where the token comes from.
        // External mode pulls it from the provider (postMessage); internal mode
        // fetches it from the burns proxy. All caching/expiry logic below is shared.
        this.activeToken = this.externalTokenProvider
            ? await this.externalTokenProvider()
            : (await axios.get(this.proxyUrl)).data;
        // Calculate expiration time with a 5-minute buffer
        // This ensures we refresh before the token actually expires
        if (this.activeToken?.expires_in !== undefined && this.activeToken.expires_in > 0) {
            const bufferSeconds = 5 * 60; // 5 minutes
            // Use 10% of TTL as buffer if it's less than 5 minutes, otherwise use 5 minutes
            const effectiveBuffer = Math.min(bufferSeconds, Math.floor(this.activeToken.expires_in * 0.1));
            const expiresIn = Math.max(0, this.activeToken.expires_in - effectiveBuffer);
            this.tokenExpiresAt = Date.now() + expiresIn * 1000;
        }
        else {
            // If expires_in is missing or invalid, set a conservative default of 50 minutes
            const defaultTTL = 50 * 60; // 50 minutes
            this.tokenExpiresAt = Date.now() + defaultTTL * 1000;
        }
        return this.activeToken;
    }
    async getToken() {
        // If a refresh is already in progress, wait for it
        if (this.refreshPromise) {
            return this.refreshPromise;
        }
        // Check if we need to fetch a new token
        if (this.shouldRefreshToken()) {
            // Create a promise for this refresh and store it
            this.refreshPromise = this.fetchNewToken().finally(() => {
                // Clear the refresh promise once complete (success or failure)
                this.refreshPromise = undefined;
            });
            return this.refreshPromise;
        }
        return this.activeToken;
    }
    isAuthenticated() {
        return !!this.activeToken && !this.shouldRefreshToken();
    }
    logout() {
        this.clearCachedToken();
        return Promise.resolve(true);
    }
    /**
     * Drops the cached token, its expiry, and any in-flight refresh so the next
     * getToken() call re-authenticates from scratch. This is the token-state
     * reset shared by logout() and the external-auth switches; it does not sign
     * the user out of anything on its own.
     */
    clearCachedToken() {
        this.activeToken = undefined;
        this.tokenExpiresAt = undefined;
        this.refreshPromise = undefined;
    }
    async authenticate(props) {
        const token = await this.getToken();
        if (token) {
            props.mode = "cors";
            delete props["credentials"];
            props.headers = {
                ...props.headers,
                Authorization: `Bearer ${token.access_token}`
            };
        }
        return props;
    }
}
//# sourceMappingURL=session.js.map